import ReactApexChart from 'react-apexcharts';
import { ChartDonut, ChartLineUp } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import moment from 'moment';
import { useActives } from '../../../../../hooks/useActives';

import { useActivesWallet } from '../../hooks/ActivesHooks';
import {
  ButtonChangeGraphic,
  CompositionGraphicWalletContainer,
  ContentButtonChangeGraphic,
  ContentGraphic,
  ContentListTitle,
} from './styles';
import { GraphicWalletActives } from '../../../../components/Boltz/MyWallet/Graphic';
import { buildBoxAdjustedComposition } from '../../../../../utils/boxAdjustedComposition';
import { buildWalletHistory } from '../../../../../utils/walletHistory';

interface IPropsCompositionGraphicWallet {
  tab: number;
}

function buildOthersFromPositions(items: any[]) {
  const valid = items.filter((it) => !it?.reservadeemergencia);
  const categoryTotal = valid.reduce(
    (sum, it) => sum + Number(it?.updateValue ?? 0),
    0
  );

  if (categoryTotal <= 0) return [];

  // percentual dentro da categoria (não da carteira inteira)
  const withPercent = valid
    .filter((it) => Number(it?.updateValue ?? 0) > 0)
    .map((it) => ({
      ...it,
      percent: (Number(it.updateValue) / categoryTotal) * 100,
    }));

  const large = withPercent.filter((it) => it.percent >= 1);
  const small = withPercent.filter((it) => it.percent < 1);

  if (small.length === 0) return large;

  const totalSmall = small.reduce((acc, it) => acc + it.percent, 0);

  return [
    ...large,
    {
      percent: totalSmall,
      tickerSymbol: 'Outros',
      Exception: false,
      ValorExp: 0,
      aportarQtde: 0,
      closingPrice: 0,
      corporationName: '',
      equitiesQuantity: 0,
      novoupdateValue: 0,
      updateValue: 0,
    },
  ];
}

export function CompositionGraphicWallet({
  tab,
}: IPropsCompositionGraphicWallet) {
  const { fullWallet, othersFii, othersAction } = useActivesWallet();
  const originalHistory = buildWalletHistory(fullWallet.rentabilidade ?? []);
  const currentTotal = fullWallet.positionsAll?.reduce(
    (total, position) => position.reservadeemergencia
      ? total
      : total + position.updateValue,
    0
  );
  const rentabilidade = buildWalletHistory(originalHistory, currentTotal);
  const hasEstimatedHistory = rentabilidade.some(
    (point, index) => point.value !== originalHistory[index].value
  );
  const knownValues = rentabilidade
    .map(item => item.value)
    .filter((value): value is number => value !== null);
  const isGrowing = knownValues.length > 1 && knownValues[knownValues.length - 1] > knownValues[0];
  const historyMetadata = fullWallet.rentabilidade_metadata;
  // @ts-ignore
  const { showActive } = useActives();
  const [typeGraphic, setTypeGraphic] = useState<'wallet' | 'rentability'>(
    'wallet'
  );

  const othersStocks = useMemo(
    () =>
      buildOthersFromPositions(
        (fullWallet?.positionsAll ?? []).filter((it: any) =>
          ['stock', 'stocks'].includes(it?.productTypeName)
        )
      ),
    [fullWallet]
  );

  const othersEtfs = useMemo(
    () =>
      buildOthersFromPositions(
        (fullWallet?.positionsAll ?? []).filter((it: any) =>
          ['etf', 'etfs'].includes(it?.productTypeName)
        )
      ),
    [fullWallet]
  );

  const othersReits = useMemo(
    () =>
      buildOthersFromPositions(
        (fullWallet?.positionsAll ?? []).filter((it: any) =>
          ['reit', 'reits'].includes(it?.productTypeName)
        )
      ),
    [fullWallet]
  );

  const othersCrypto = useMemo(
    () =>
      buildOthersFromPositions(
        (fullWallet?.positionsAll ?? []).filter((it: any) =>
          ['crypto', 'cripto'].includes(
            String(it?.productTypeName).toLowerCase()
          )
        )
      ),
    [fullWallet]
  );

  const dataGraphicRentabilidade = {
    series: [
      {
        name: 'Patrimônio',
        data: rentabilidade?.map((rent) => rent.value) ?? [0],
      },
    ],
    options: {
      chart: {
        type: 'area',
        height: 350,
        zoom: {
          enabled: false,
        },
      },
      stroke: {
        curve: 'smooth',
      },

      grid: {
        padding: {
          bottom: 0,
          top: 0,
          right: 0,
          left: 0,
        },
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: false,
      },
      theme: {
        mode: 'light',
        monochrome: {
          enabled: true,
          color:
            rentabilidade &&
            rentabilidade.length >= 1 &&
            isGrowing
              ? '#303F4B'
              : '#EC6643',
        },
      },
      yaxis: {
        labels: {
          formatter: (val: number) =>
            new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(val),
        },
      },
      colors:
        rentabilidade && rentabilidade.length >= 1
          ? [
              isGrowing
                ? '#303F4B'
                : 'red',
            ]
          : ['green'],

      labels: rentabilidade?.map((rent) =>
        moment(rent.referenceDate).format('l')
      ) ?? [''],
      // responsive: [
      //   {
      //     breakpoint: 480,
      //     options: {
      //       chart: {
      //         width: 200,
      //       },
      //       legend: {
      //         position: 'bottom',
      //       },
      //     },
      //   },
      // ],
    },
  };
  const dataGraphicRentabilidadeNotShowValue = {
    series: [
      {
        name: 'Patrimônio',
        data: rentabilidade?.map((rent) => rent.value) ?? [0],
      },
    ],
    options: {
      chart: {
        type: 'area',
        height: 350,
        zoom: {
          enabled: false,
        },
      },
      stroke: {
        curve: 'smooth',
      },

      grid: {
        padding: {
          bottom: 0,
          top: 0,
          right: 0,
          left: 0,
        },
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: false,
      },
      theme: {
        mode: 'light',
        monochrome: {
          enabled: true,
          color:
            rentabilidade &&
            rentabilidade.length >= 1 &&
            isGrowing
              ? '#303F4B'
              : '#f11414',
        },
      },
      yaxis: {
        labels: {
          formatter: (val: number) => 'R$ *******',
        },
      },
      colors:
        rentabilidade && rentabilidade.length >= 1
          ? [
              isGrowing
                ? '#303F4B'
                : 'red',
            ]
          : ['green'],

      labels: rentabilidade?.map((rent) =>
        moment(rent.referenceDate).format('l')
      ) ?? [''],
      // responsive: [
      //   {
      //     breakpoint: 480,
      //     options: {
      //       chart: {
      //         width: 200,
      //       },
      //       legend: {
      //         position: 'bottom',
      //       },
      //     },
      //   },
      // ],
    },
  };
  // Categorias da composição — recomputadas a partir das posições para
  // DESCONSIDERAR a parte alocada em boxes (via box_applied_percentage). Cada
  // ativo entra apenas pelo valor livre (fora de boxes). Internacionais/categorias
  // só aparecem quando têm valor, para não poluir o gráfico/legenda.
  const compositionCategories = useMemo(() => {
    const adjusted = buildBoxAdjustedComposition(fullWallet?.positionsAll ?? []);

    return [
      { key: 'fii', label: 'FIIs', color: '#303F4B', value: adjusted.fii },
      { key: 'action', label: 'Ações', color: '#ffb900', value: adjusted.action },
      {
        key: 'rendaFixa',
        label: 'Renda fixa',
        color: '#FF8B4B',
        value: adjusted.rendaFixa,
      },
      { key: 'stocks', label: 'Stocks', color: '#1FA75B', value: adjusted.stocks },
      { key: 'etfs', label: 'ETFs', color: '#0FA3B1', value: adjusted.etfs },
      { key: 'reits', label: 'REITs', color: '#7B61FF', value: adjusted.reits },
      {
        key: 'crypto',
        label: 'Criptomoedas',
        color: '#295074',
        value: adjusted.crypto,
      },
    ].filter((cat) => Number(cat.value) > 0);
  }, [fullWallet]);

  const dataGraphic = {
    series: compositionCategories.map((cat) => cat.value),
    options: {
      chart: {
        width: 380,
        type: 'pie',
      },
      plotOptions: {
        pie: {
          dataLabels: {
            offset: -10,
          },
        },
      },
      grid: {
        padding: {
          bottom: 0,
          top: 0,
          right: 0,
          left: 0,
        },
      },
      colors: compositionCategories.map((cat) => cat.color),
      legend: {
        show: false,
      },
      labels: compositionCategories.map((cat) => cat.label),
      // responsive: [
      //   {
      //     breakpoint: 480,
      //     options: {
      //       chart: {
      //         width: 200,
      //       },
      //       legend: {
      //         position: 'bottom',
      //       },
      //     },
      //   },
      // ],
    },
  };

  return (
    <CompositionGraphicWalletContainer>
      <header>
        <strong>
          {typeGraphic === 'wallet'
            ? 'Composição da carteira'
            : 'Evolução do patrimônio'}
        </strong>
        <ContentButtonChangeGraphic>
          <ButtonChangeGraphic
            type="button"
            isActive={typeGraphic === 'wallet'}
            onClick={() => setTypeGraphic('wallet')}
          >
            <ChartDonut />
          </ButtonChangeGraphic>
          <ButtonChangeGraphic
            type="button"
            isActive={typeGraphic === 'rentability'}
            onClick={() => setTypeGraphic('rentability')}
          >
            <ChartLineUp />
          </ButtonChangeGraphic>
        </ContentButtonChangeGraphic>
      </header>
      {typeGraphic === 'rentability' && (
        <>
        {!hasEstimatedHistory && historyMetadata?.manual_history_unavailable ? (
          <p role="status">Histórico parcial: os saldos manuais sem histórico não estão incluídos.</p>
        ) : null}
        {(historyMetadata?.international_positions_without_trades ||
          Boolean(historyMetadata?.international_missing_dates?.length)) && (
          <p role="status">Há posições internacionais ou cotações sem histórico disponível.</p>
        )}
        <ReactApexChart
          // @ts-ignore
          options={
            showActive
              ? dataGraphicRentabilidade.options
              : dataGraphicRentabilidadeNotShowValue.options
          }
          series={dataGraphicRentabilidade.series}
          type="area"
          height={300}
        />
        </>
      )}
      {typeGraphic === 'wallet' &&
        ![2, 3, 5, 6, 7, 8].includes(tab) && (
          <ContentGraphic>
            <ReactApexChart
              // @ts-ignore
              options={dataGraphic.options}
              series={dataGraphic.series}
              type="pie"
              width={200}
            />
            <ul>
              {compositionCategories.map((cat) => (
                <li key={cat.key}>
                  <ContentListTitle color={cat.color}>
                    {cat.label}
                  </ContentListTitle>
                  <strong>{cat.value}%</strong>
                </li>
              ))}
            </ul>
          </ContentGraphic>
        )}
      {typeGraphic === 'wallet' && tab === 3 && (
        <GraphicWalletActives
          active="fii"
          title="Distribuição por ativo"
          othersFii={othersFii}
        />
      )}
      {typeGraphic === 'wallet' && tab === 2 && (
        <GraphicWalletActives
          active="action"
          othersAction={othersAction}
          title="Distribuição por ativo"
        />
      )}
      {typeGraphic === 'wallet' && tab === 5 && (
        <GraphicWalletActives
          active="stocks"
          othersStocks={othersStocks}
          title="Distribuição por ativo"
        />
      )}
      {typeGraphic === 'wallet' && tab === 6 && (
        <GraphicWalletActives
          active="reits"
          othersReits={othersReits}
          title="Distribuição por ativo"
        />
      )}

      {typeGraphic === 'wallet' && tab === 7 && (
        <GraphicWalletActives
          active="etfs"
          othersEtfs={othersEtfs}
          title="Distribuição por ativo"
        />
      )}

      {typeGraphic === 'wallet' && tab === 8 && (
        <GraphicWalletActives
          active="crypto"
          othersCrypto={othersCrypto}
          title="Distribuição por ativo"
        />
      )}
    </CompositionGraphicWalletContainer>
  );
}
